import { AssetTypes } from '../studioContext/CMSParams';

export const getUploadSource = ({ assetType, storyId, resourceId } = {}) => {
  if (assetType === AssetTypes.Story && storyId) {
    return { assetType, storyId };
  }
  if (
    resourceId &&
    [
      AssetTypes.StoryThumbnail,
      AssetTypes.StoryIcon,
      AssetTypes.StoryAnimatedThumbnail,
      AssetTypes.StoryCircularAnimatedThumbnail,
    ].includes(assetType)
  ) {
    return { assetType, storyId: resourceId };
  }
  if (
    resourceId &&
    [
      AssetTypes.Clip,
      AssetTypes.ClipThumbnail,
      AssetTypes.ClipAnimatedThumbnail,
    ].includes(assetType)
  ) {
    return { assetType, clipId: resourceId };
  }
  return null;
};

const isMediaElement = ({ type } = {}) =>
  type === 'image' || type === 'video' || type === 'gif';

// Session-only provenance. This state must never enter story JSON or undo history.
export const createUploadSession = (isCurrent = () => true) => {
  const uploads = new Map();
  const aliases = new Map();
  let active = true;

  return {
    add(uploadId, resource) {
      if (!active || !isCurrent() || !resource?.id || !resource?.src) return;
      if (uploads.has(uploadId)) return;
      uploads.set(uploadId, {
        resource: {
          src: resource.src,
          mimeType: resource.mimeType,
          title: resource.title,
        },
      });
      aliases.set(uploadId, uploadId);
      aliases.set(resource.id, uploadId);
    },

    derive(originalResourceId, resourceId) {
      const uploadId = aliases.get(originalResourceId);
      if (active && isCurrent() && uploads.has(uploadId) && resourceId) {
        aliases.set(resourceId, uploadId);
      }
    },

    snapshot(pages = []) {
      if (!active || !isCurrent()) return { uploads: [], consideredIds: [] };
      const selected = new Map();
      for (const page of pages) {
        if (page.isProcessing) continue;
        for (const element of page.elements || []) {
          if (!isMediaElement(element)) continue;
          const uploadId = aliases.get(element.resource?.id);
          const upload = uploads.get(uploadId);
          if (!upload || selected.has(uploadId)) continue;
          selected.set(uploadId, {
            uploadId,
            ...upload,
            // Editor identity, explicitly not a CMS page ID. Backend resolves it.
            ampId: page.id,
          });
        }
      }
      return {
        uploads: [...selected.values()],
        consideredIds: [...uploads.keys()],
      };
    },

    saved(snapshot) {
      // Includes discarded uploads: eligibility is fixed at the first successful
      // save. Uploads completed after the snapshot belong to a subsequent save.
      for (const id of snapshot.consideredIds) uploads.delete(id);
      for (const [alias, id] of aliases) {
        if (!uploads.has(id)) aliases.delete(alias);
      }
    },

    dispose() {
      active = false;
      uploads.clear();
      aliases.clear();
    },
  };
};
